import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Circle, Plus, X, Calendar, Edit2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { BucketItem } from '../../types';

const BucketListScreen: React.FC = () => {
  const { bucketItems, addBucketItem, updateBucketItem, toggleBucketItem } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<BucketItem | null>(null);

  // Form state
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('여행');
  const [newTargetDate, setNewTargetDate] = useState('');

  const categories = ['여행', '활동', '기록', '액티비티', '기타'];

  const completedCount = bucketItems.filter(item => item.isCompleted).length;
  const progress = bucketItems.length > 0 ? Math.round((completedCount / bucketItems.length) * 100) : 0;

  useEffect(() => {
    if (editingItem) {
      setNewTitle(editingItem.title);
      setNewDescription(editingItem.description || '');
      setNewCategory(editingItem.category);
      setNewTargetDate(editingItem.targetDate || '');
    } else {
      setNewTitle('');
      setNewDescription('');
      setNewCategory('여행');
      setNewTargetDate('');
    }
  }, [editingItem, isModalOpen]);

  const handleOpenAddModal = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item: BucketItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!newTitle.trim()) return;

    const itemData = {
      title: newTitle,
      description: newDescription || undefined,
      category: newCategory,
      targetDate: newTargetDate || undefined,
    };

    if (editingItem) {
      await updateBucketItem(editingItem.id, itemData);
    } else {
      await addBucketItem({
        ...itemData,
        isCompleted: false,
      });
    }

    setIsModalOpen(false);
  };

  return (
    <div className="flex flex-col gap-6 pb-24 relative">
      <header className="flex justify-between items-center py-4">
        <h1 className="text-2xl font-bold text-charcoal text-left">버킷리스트</h1>
      </header>

      {/* Floating Action Button */}
      <button 
        onClick={handleOpenAddModal}
        className="fixed bottom-24 right-6 bg-primary text-white p-4 rounded-full shadow-2xl active:scale-95 transition-all z-40"
      >
        <Plus size={24} strokeWidth={3} />
      </button>

      {/* Progress Card */}
      <div className="card bg-primary text-white border-none overflow-hidden relative p-6">
        <div className="relative z-10 text-left">
          <p className="text-xs opacity-80 font-bold uppercase tracking-wider">우리의 꿈들</p>
          <h2 className="text-4xl font-black mt-1">{progress}%</h2>
          <div className="w-full bg-white/20 h-2.5 rounded-full mt-5 overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              className="bg-white h-full" 
            />
          </div>
          <p className="text-[10px] mt-3 font-bold opacity-70 text-right uppercase">
            {bucketItems.length}개 중 {completedCount}개 달성
          </p>
        </div>
        <CheckCircle2 className="absolute -right-6 -bottom-6 opacity-10" size={140} />
      </div>

      <div className="flex flex-col gap-3">
        {bucketItems.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <p className="text-sm">아직 등록된 버킷리스트가 없어요.</p>
          </div>
        ) : (
          bucketItems.map((item, index) => (
            <motion.div 
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => handleOpenEditModal(item)}
              className={`card flex items-center gap-4 border-none shadow-sm transition-all cursor-pointer hover:shadow-md ${item.isCompleted ? 'bg-secondary/30' : 'bg-white'}`}
            >
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleBucketItem(item.id);
                }}
                className={`transition-transform active:scale-90 ${item.isCompleted ? 'text-primary' : 'text-gray-200'}`}
              >
                {item.isCompleted ? (
                  <CheckCircle2 size={26} fill="currentColor" stroke="white" strokeWidth={2.5} />
                ) : (
                  <Circle size={26} strokeWidth={2.5} />
                )}
              </button>
              <div className="flex-1 text-left">
                <div className="flex justify-between items-start">
                   <p className={`font-bold text-[14px] ${item.isCompleted ? 'line-through text-gray-400' : 'text-charcoal'}`}>
                    {item.title}
                  </p>
                  <Edit2 size={14} className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className={`text-[9px] font-black uppercase tracking-tighter px-2 py-0.5 rounded-md ${
                    item.isCompleted ? 'bg-gray-100 text-gray-300' : 'bg-secondary text-primary/60'
                  }`}>
                    {item.category}
                  </span>
                  {item.targetDate && (
                    <span className="text-[9px] font-bold text-gray-400 flex items-center gap-1">
                      <Calendar size={10} />
                      {item.targetDate.replace(/-/g, '.')}까지
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Add/Edit Bucket Item Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative w-full max-w-sm bg-white rounded-t-[40px] sm:rounded-[40px] p-8 shadow-2xl"
            >
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-black text-charcoal">
                  {editingItem ? '버킷리스트 수정' : '버킷리스트 추가'}
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="p-2 bg-secondary/50 rounded-full text-gray-400 hover:text-charcoal transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">하고 싶은 일</label>
                  <input 
                    type="text" 
                    placeholder="예: 제주도 한 달 살기"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="w-full bg-secondary border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-bold"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">목표 날짜 (선택)</label>
                  <input 
                    type="date" 
                    value={newTargetDate}
                    onChange={(e) => setNewTargetDate(e.target.value)}
                    className="w-full bg-secondary border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-bold"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">카테고리</label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setNewCategory(cat)}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                          newCategory === cat 
                          ? 'bg-primary text-white shadow-md shadow-primary/20' 
                          : 'bg-secondary text-gray-400 hover:text-charcoal'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">상세 내용 (선택)</label>
                  <textarea 
                    rows={3}
                    placeholder="상세한 계획이나 메모를 남겨보세요"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    className="w-full bg-secondary border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-medium resize-none"
                  />
                </div>

                <button 
                  onClick={handleSave}
                  disabled={!newTitle.trim()}
                  className="w-full bg-primary text-white font-black py-5 rounded-2xl shadow-xl shadow-primary/30 active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100 text-sm"
                >
                  {editingItem ? '변경사항 저장하기' : '리스트에 추가하기'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BucketListScreen;
